import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Download, 
  Calendar,
  BarChart3,
  Users,
  Shield,
  Activity,
  DollarSign,
  Search,
  Filter,
  Eye,
  ChevronRight,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';

const SuperAdminReporting = () => {
  const [selectedReport, setSelectedReport] = useState('');
  const [dateRange, setDateRange] = useState('last30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [filterInvestor, setFilterInvestor] = useState('all');
  const [filterBond, setFilterBond] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [activeTab, setActiveTab] = useState('generate');
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState(null);

  // Mock data for report types
  const reportTypes = [
    {
      id: 'platform_overview',
      name: 'Platform Overview Report',
      description: 'Comprehensive overview of all platform activities and metrics',
      icon: BarChart3,
      estimatedTime: '3-5 minutes'
    },
    {
      id: 'user_management',
      name: 'User Management Report',
      description: 'Detailed analysis of user registrations, roles, and activities',
      icon: Users,
      estimatedTime: '2-3 minutes'
    },
    {
      id: 'system_performance',
      name: 'System Performance Report',
      description: 'Platform performance metrics and infrastructure health',
      icon: Activity,
      estimatedTime: '1-2 minutes'
    },
    {
      id: 'bond_issuance_summary',
      name: 'Bond Issuance Summary Report',
      description: 'Aggregated data on all bonds issued through the platform',
      icon: TrendingUp,
      estimatedTime: '2-4 minutes'
    },
    {
      id: 'security_incident',
      name: 'Security Incident Report',
      description: 'Details of detected security threats and incidents',
      icon: Shield,
      estimatedTime: '2-3 minutes'
    },
    {
      id: 'audit_log',
      name: 'Audit Log Report',
      description: 'Comprehensive log of all system and user actions',
      icon: FileText,
      estimatedTime: '4-6 minutes'
    },
    {
      id: 'compliance_report',
      name: 'Compliance & Regulatory Report',
      description: 'Summary of compliance activities and regulatory reporting',
      icon: Shield,
      estimatedTime: '3-4 minutes'
    }
  ];

  // Mock data for investors and bonds
  const investors = [
    { id: 'INV-001', name: 'Ahmad bin Rahman', type: 'Individual' },
    { id: 'INV-002', name: 'Tech Solutions Sdn Bhd', type: 'Corporate' },
    { id: 'INV-003', name: 'Jennifer Wong Su Ling', type: 'Individual' },
    { id: 'INV-004', name: 'Global Investment Ltd', type: 'Corporate' }
  ];

  const bonds = [
    'Green Infrastructure Bond 2024-A',
    'Tech Development Sukuk',
    'Infrastructure Bond 2022-A',
    'Corporate Expansion Bond',
    'Renewable Energy Sukuk Series B'
  ];

  // Mock data for generated reports
  const [reportsHistory] = useState([
    {
      id: 'RPT-001',
      reportType: 'Platform Overview Report',
      generatedDate: '2024-01-22T09:15:00Z',
      dateRange: 'January 2024',
      parameters: {
        startDate: '2024-01-01',
        endDate: '2024-01-22',
        investor: 'All Investors',
        bond: 'All Sukuk'
      },
      status: 'completed',
      downloadUrl: '#',
      generatedBy: 'super.admin@platform.com',
      fileSize: '5.2 MB'
    },
    {
      id: 'RPT-002',
      reportType: 'User Management Report',
      generatedDate: '2024-01-15T14:30:00Z',
      dateRange: 'Last 30 Days',
      parameters: {
        startDate: '2023-12-16',
        endDate: '2024-01-15',
        investor: 'All Investors',
        bond: 'All Bonds'
      },
      status: 'completed',
      downloadUrl: '#',
      generatedBy: 'super.admin@platform.com',
      fileSize: '2.3 MB'
    },
    {
      id: 'RPT-003',
      reportType: 'System Performance Report',
      generatedDate: '2024-01-14T11:45:00Z',
      dateRange: 'Custom Range',
      parameters: {
        startDate: '2024-01-01',
        endDate: '2024-01-14',
        investor: 'All Investors',
        bond: 'Green Infrastructure Bond 2024-A'
      },
      status: 'completed',
      downloadUrl: '#',
      generatedBy: 'super.admin@platform.com',
      fileSize: '4.1 MB'
    },
    {
      id: 'RPT-004',
      reportType: 'Security Incident Report',
      generatedDate: '2024-01-13T16:20:00Z',
      dateRange: 'Last 7 Days',
      parameters: {
        startDate: '2024-01-06',
        endDate: '2024-01-13',
        investor: 'All Investors',
        bond: 'All Bonds'
      },
      status: 'completed',
      downloadUrl: '#',
      generatedBy: 'super.admin@platform.com',
      fileSize: '1.8 MB'
    },
    {
      id: 'RPT-005',
      reportType: 'Bond Issuance Summary Report',
      generatedDate: '2024-01-25T10:30:00Z',
      dateRange: 'January 2025',
      parameters: {
        startDate: '2025-01-01',
        endDate: '2025-01-31',
        investor: 'All Investors',
        bond: 'Green Sukuk Infrastructure 2027'
      },
      status: 'completed',
      downloadUrl: '#',
      generatedBy: 'super.admin@platform.com',
      fileSize: '3.2 MB'
    }
  ]);

  // Mock data for audit logs
  const [auditLogs] = useState([
    {
      id: 'AUDIT-001',
      timestamp: '2024-01-15T14:30:00Z',
      user: 'super.admin@platform.com',
      action: 'User Role Updated',
      details: 'Updated role for Ahmad bin Rahman (INV-001) to Premium Investor',
      ipAddress: '192.168.1.100',
      category: 'USER_MANAGEMENT',
      result: 'SUCCESS'
    },
    {
      id: 'AUDIT-002',
      timestamp: '2024-01-15T13:45:00Z',
      user: 'super.admin@platform.com',
      action: 'System Configuration Updated',
      details: 'Updated platform security settings and rate limits',
      ipAddress: '192.168.1.100',
      category: 'SYSTEM_CONFIG',
      result: 'SUCCESS'
    },
    {
      id: 'AUDIT-003',
      timestamp: '2024-01-15T12:20:00Z',
      user: 'super.admin@platform.com',
      action: 'Bond Approval',
      details: 'Approved new bond issuance: Green Infrastructure Bond 2024-A',
      ipAddress: '192.168.1.100',
      category: 'BOND_MANAGEMENT',
      result: 'SUCCESS'
    },
    {
      id: 'AUDIT-004',
      timestamp: '2024-01-15T11:10:00Z',
      user: 'super.admin@platform.com',
      action: 'Security Alert',
      details: 'Investigated suspicious login attempt from IP 203.45.67.89',
      ipAddress: '192.168.1.100',
      category: 'SECURITY',
      result: 'SUCCESS'
    },
    {
      id: 'AUDIT-005',
      timestamp: '2024-01-15T10:30:00Z',
      user: 'unknown@platform.com',
      action: 'Login Attempt Failed',
      details: 'Failed login attempt with invalid credentials',
      ipAddress: '203.45.67.89',
      category: 'AUTHENTICATION',
      result: 'FAILURE'
    }
  ]);

  const tabs = [
    {
      id: 'generate',
      name: 'Generate Reports',
      icon: FileText,
      description: 'Create new reports with custom parameters'
    },
    {
      id: 'audit',
      name: 'Audit Logs',
      icon: Shield,
      description: 'View system audit trails and user activities'
    }
  ];

  const handleGenerateReport = () => {
    if (!selectedReport) return;
    
    setIsGenerating(true);
    
    // Simulate report generation
    setTimeout(() => {
      const newReport = {
        id: `RPT-${String(reportsHistory.length + 1).padStart(3, '0')}`,
        reportType: reportTypes.find(r => r.id === selectedReport)?.name,
        generatedDate: new Date().toISOString(),
        dateRange: dateRange === 'custom' ? 'Custom Range' : dateRange.replace(/([a-z])([A-Z])/g, '$1 $2'),
        parameters: {
          startDate: dateRange === 'custom' ? customStartDate : getDateRangeStart(),
          endDate: dateRange === 'custom' ? customEndDate : new Date().toISOString().split('T')[0],
          investor: filterInvestor === 'all' ? 'All Investors' : investors.find(i => i.id === filterInvestor)?.name,
          bond: filterBond === 'all' ? 'All Bonds' : filterBond
        },
        status: 'completed',
        downloadUrl: '#',
        generatedBy: 'super.admin@platform.com',
        fileSize: `${Math.floor(Math.random() * 5) + 1}.${Math.floor(Math.random() * 9)} MB`
      };
      
      setGeneratedReports([newReport, ...generatedReports]);
      setIsGenerating(false);
    }, 3000);
  };

  const handleViewReport = (report) => {
    // Handle report viewing logic here
    console.log('Viewing report:', report);
  };

  const getDateRangeStart = () => {
    const today = new Date();
    switch (dateRange) {
      case 'last7days':
        return new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
      case 'last30days':
        return new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
      case 'last90days':
        return new Date(today.setDate(today.getDate() - 90)).toISOString().split('T')[0];
      default:
        return new Date().toISOString().split('T')[0];
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAuditCategoryBadge = (category) => {
    const categoryClasses = {
      USER_MANAGEMENT: 'bg-blue-100 text-blue-800',
      SYSTEM_CONFIG: 'bg-purple-100 text-purple-800',
      BOND_MANAGEMENT: 'bg-green-100 text-green-800',
      SECURITY: 'bg-yellow-100 text-yellow-800',
      AUTHENTICATION: 'bg-red-100 text-red-800',
      SYSTEM: 'bg-gray-100 text-gray-800'
    };
    return categoryClasses[category] || 'bg-gray-100 text-gray-800';
  };

  const getResultBadge = (result) => {
    return result === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };



  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reporting & Audit Trails</h1>
              <p className="text-sm text-gray-500 mt-1">Generate comprehensive reports and access detailed audit logs for compliance and monitoring</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Generate Reports Tab */}
        {activeTab === 'generate' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Report Selection */}
              <div className="lg:col-span-2">
                <div className="bg-white border border-light-gray rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Select Report Type</h3>
                  <div className="space-y-3">
                    {reportTypes.map((report) => {
                      const Icon = report.icon;
                      return (
                        <label
                          key={report.id}
                          className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedReport === report.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="reportType"
                            value={report.id}
                            checked={selectedReport === report.id}
                            onChange={(e) => setSelectedReport(e.target.value)}
                            className="mt-1 mr-3"
                          />
                          <Icon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{report.name}</div>
                            <div className="text-sm text-gray-500 mt-1">{report.description}</div>
                            <div className="text-xs text-gray-400 mt-1">
                              Estimated generation time: {report.estimatedTime}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Report Parameters */}
              <div className="space-y-6">
                {/* Date Range */}
                <div className="bg-white border border-light-gray rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Date Range</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last7days"
                        checked={dateRange === 'last7days'}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 7 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last30days"
                        checked={dateRange === 'last30days'}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 30 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last90days"
                        checked={dateRange === 'last90days'}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 90 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="custom"
                        checked={dateRange === 'custom'}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Custom Range</span>
                    </label>
                    
                    {dateRange === 'custom' && (
                      <div className="mt-3 space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Filters */}
                <div className="bg-white border border-light-gray rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Investor</label>
                      <select
                        value={filterInvestor}
                        onChange={(e) => setFilterInvestor(e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="all">All Investors</option>
                        {investors.map(investor => (
                          <option key={investor.id} value={investor.id}>
                            {investor.name} ({investor.type})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bond</label>
                      <select
                        value={filterBond}
                        onChange={(e) => setFilterBond(e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="all">All Bonds</option>
                        {bonds.map(bond => (
                          <option key={bond} value={bond}>{bond}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="bg-white shadow rounded-lg p-6">
                  <button
                    onClick={handleGenerateReport}
                    disabled={!selectedReport || isGenerating}
                    className="w-full btn-primary"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </button>
                  
                 
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6">
            <div className="bg-white border border-light-gray rounded-lg overflow-hidden overflow-x-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">System Audit Trail</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Comprehensive log of all platform actions, user data changes, and system transactions.
                </p>
              </div>
              
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Timestamp
                    </th>
                    <th className="table-header-cell">
                      User
                    </th>
                    <th className="table-header-cell">
                      Action
                    </th>
                    <th className="table-header-cell">
                      Category
                    </th>
                    <th className="table-header-cell">
                      Result
                    </th>
                    <th className="table-header-cell">
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(log.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {typeof log.user === 'string' ? log.user : 'Unknown User'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{log.action}</div>
                          <div className="text-gray-500">{log.details}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAuditCategoryBadge(log.category)}`}>
                          {log.category.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getResultBadge(log.result)}`}>
                          {log.result === 'SUCCESS' ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : (
                            <AlertTriangle className="h-3 w-3 mr-1" />
                          )}
                          {log.result}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {log.ipAddress}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminReporting; 